import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './TuteeSubscriptions.css';

const API = 'https://mykaddle-api.onrender.com/api';

const fmt = (d) => d
  ? new Date(d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long' })
  : ' - ';

const RenewIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
    <g fill="currentColor">
      <path d="M5.254 14.596a.5.5 0 0 1 .707-.707A5.5 5.5 0 0 0 15.35 10a.5.5 0 0 1 .999.001a6.5 6.5 0 0 1-11.096 4.596"/>
      <path d="M13.131 12.416a.5.5 0 0 1-.555-.832l3-2a.5.5 0 1 1 .555.832z"/>
      <path d="M18.266 12.723a.5.5 0 1 1-.832.554l-2-3a.5.5 0 0 1 .832-.554zm-3.912-7.518a.5.5 0 0 1-.708.707a5.5 5.5 0 0 0-9.389 3.89a.5.5 0 0 1-1-.001a6.5 6.5 0 0 1 11.097-4.596"/>
      <path d="M6.476 7.385a.5.5 0 0 1 .555.832l-3 2a.5.5 0 1 1-.555-.832z"/>
      <path d="M1.341 7.078a.5.5 0 1 1 .832-.554l2 3a.5.5 0 0 1-.832.554z"/>
    </g>
  </svg>
);

const TuteeSubscriptions = () => {
  const navigate = useNavigate();

  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [supportOpen, setSupportOpen] = useState(false);

  const token = localStorage.getItem('token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchSubs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/tutee/subscriptions`, { headers: authHeaders });
      const data = await res.json();
      const visible = (Array.isArray(data) ? data : [])
        .filter(s => s.status !== 'left');
      setSubscriptions(visible);
    } catch (err) {
      console.error('fetchSubs error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSubs(); }, [fetchSubs]);

  useEffect(() => {
    if (!supportOpen) return;
    const t = setTimeout(() => setSupportOpen(false), 10000);
    return () => clearTimeout(t);
  }, [supportOpen]);

  // ── Navigate to payment page instead of opening modal ──
  const navigateToPayment = (sub) => {
    if (!sub.rate || sub.rate <= 0) {
      alert('Cannot process payment — subscription rate not found. Please contact support.');
      return;
    }
    navigate('/payment/checkout', {
      state: {
        subjectId: sub.subjectId,
        subjectName: sub.subjectDisplayName,
        subscriptionAmount: sub.rate || 0,
        billingCycle: sub.billingCycle || 'monthly',
      }
    });
  };

  const getAction = (sub) => {
    if (sub.status === 'trial') {
      return (
        <button className="sub-action-btn sub-subscribe-btn" onClick={() => navigateToPayment(sub)}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <path fill="currentColor" d="M2.316 3.25a.75.75 0 1 0 0 1.5h1.181a.75.75 0 0 1 .743.646l1.254 8.917a2.25 2.25 0 0 0 2.228 1.937h10.344a.75.75 0 0 0 0-1.5H7.722a.75.75 0 0 1-.743-.646l-.12-.853h10.852a2.25 2.25 0 0 0 2.15-1.583l1.921-6.188a.75.75 0 0 0-.716-.972H5.516A2.25 2.25 0 0 0 3.498 3.25zm3.525 2.758h14.207l-1.62 5.215a.75.75 0 0 1-.717.527H6.648zM7.784 17.75a1.75 1.75 0 1 0 0 3.5a1.75 1.75 0 0 0 0-3.5m6.786 1.75a1.75 1.75 0 1 1 3.5 0a1.75 1.75 0 0 1-3.5 0"/>
          </svg>
          Subscribe
        </button>
      );
    }
    if (sub.status === 'paid') {
      return (
        <button className="sub-action-btn sub-renew-btn sub-active" disabled>
          <RenewIcon /> Renew
        </button>
      );
    }
    if (sub.status === 'locked') {
      return (
        <button className="sub-action-btn sub-renew-btn" onClick={() => navigateToPayment(sub)}>
          <RenewIcon /> Renew
        </button>
      );
    }
    return null;
  };

  const getStatusLabel = (status) => {
    if (status === 'trial') return <span className="sub-status-active">active</span>;
    if (status === 'paid') return <span className="sub-status-active">active</span>;
    return <span className="sub-status-inactive">inactive</span>;
  };

  const getStartDate = (sub) => fmt(sub.trialStartedAt || sub.periodStart);
  const getEndDate = (sub) => fmt(sub.trialEndsAt || sub.currentPeriodEnd || sub.periodEnd);

  return (
    <div className="sub-dashboard-container">
      <div className="sub-almighty-wrapper">

        {/* ── TOP BAR ── */}
        <div className="sub-top-bar">
          <div className="sub-top-left-header">
            <div className="sub-header-row">
              <button className="sub-back-icon-btn" aria-label="Go back" onClick={() => navigate(-1)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="0.5em" height="1em" viewBox="0 0 12 24">
                  <path d="M0 0h12v24H0z" fill="none"/>
                  <path fill="currentColor" fillRule="evenodd" d="M10 19.438L8.955 20.5l-7.666-7.79a1.02 1.02 0 0 1 0-1.42L8.955 3.5L10 4.563L2.682 12z"/>
                </svg>
              </button>
              <span className="sub-page-title">Subscriptions</span>
            </div>
            <p className="sub-sub-header">Manage all your group subscriptions here.</p>
          </div>
        </div>

        {/* ── SUBSCRIPTIONS TABLE ── */}
        <div className="sub-items">
          {/* Column Headers */}
          <div className="sub-table-header">
            <span className="sub-col">Subject</span>
            <span className="sub-col">Tutor</span>
            <span className="sub-col">Billing Cycle</span>
            <span className="sub-col">Start Date</span>
            <span className="sub-col">End Date</span>
            <span className="sub-col">Status</span>
            <span className="sub-col">Action</span>
          </div>

          {/* Table Body */}
          <div className="sub-table-body">
            {loading ? (
              <p className="sub-empty-state">Loading your subscriptions…</p>
            ) : subscriptions.length === 0 ? (
              <p className="sub-empty-state">No subscriptions yet, find a tutor to get started.</p>
            ) : (
              subscriptions.map(sub => (
                <div key={sub.subjectId} className="sub-table-row">
                  <span className="sub-col">{sub.subjectDisplayName}</span>
                  <span className="sub-col">{sub.tutorName}</span>
                  <span className="sub-col">{sub.billingCycle === 'weekly' ? 'Weekly' : 'Monthly'}</span>
                  <span className="sub-col">{getStartDate(sub)}</span>
                  <span className="sub-col">{getEndDate(sub)}</span>
                  <span className="sub-col">{getStatusLabel(sub.status)}</span>
                  <span className="sub-col">{getAction(sub)}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* ── INFO BAR ── */}
        <div className="sub-info-bar">
          <div className="sub-info-bar-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
              <path d="M0 0h24v24H0z" fill="none"/>
              <path fill="#d7df23" d="M12 2.5c-3.81 0-6.5 2.743-6.5 6.119c0 1.536.632 2.572 1.425 3.56c.172.215.347.422.527.635l.096.112c.21.25.427.508.63.774c.404.531.783 1.128.995 1.834a.75.75 0 0 1-1.436.432c-.138-.46-.397-.89-.753-1.357a18 18 0 0 0-.582-.714l-.092-.11c-.18-.212-.37-.436-.555-.667C4.87 12.016 4 10.651 4 8.618C4 4.363 7.415 1 12 1s8 3.362 8 7.619c0 2.032-.87 3.397-1.755 4.5c-.185.23-.375.454-.555.667l-.092.109c-.21.248-.405.481-.582.714c-.356.467-.615.898-.753 1.357a.751.751 0 0 1-1.437-.432c.213-.706.592-1.303.997-1.834c.202-.266.419-.524.63-.774l.095-.112c.18-.213.355-.42.527-.634c.793-.99 1.425-2.025 1.425-3.561C18.5 5.243 15.81 2.5 12 2.5M8.75 18h6.5a.75.75 0 0 1 0 1.5h-6.5a.75.75 0 0 1 0-1.5m.75 3.75a.75.75 0 0 1 .75-.75h3.5a.75.75 0 0 1 0 1.5h-3.5a.75.75 0 0 1-.75-.75"/>
            </svg>
          </div>
          <p className="sub-info-bar-text">
            Group access depends on your <strong>active/inactive</strong> subscription status. You can leave a group once your subscription or free trial ends — click the leave button that appears at that time. After <strong>3 days</strong> of inactive status, you'll be auto-removed. You can always rejoin on the Discover tab.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TuteeSubscriptions;